// src/modules/LiberaProximaVenda/SelectionForm.jsx
import React, { useState, useEffect } from 'react';
import './SelectionForm.css';
import { fetchBranches } from './services/branchService';
import { fetchCustomers } from './services/customerService';
import { searchBudget, searchServiceOrder } from './services/documentService';
import Preloader from '../../components/template/Preloader';
import DocumentGrid from './DocumentGrid';

const SelectionForm = ({ onDocumentSelect }) => {
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedDocumentType, setSelectedDocumentType] = useState('');
  const [documentNumber, setDocumentNumber] = useState('');
  const [branches, setBranches] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  // Document types
  const documentTypes = [
    { id: 'budget', name: 'Orçamento', type: 1 },
    { id: 'serviceOrder', name: 'Ordem de Serviço', type: 2 },
  ];

  // Fetch branches and customers on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        const branchesData = await fetchBranches();
        const customersData = await fetchCustomers();

        setBranches(branchesData);
        setCustomers(customersData);

        setError(null);
      } catch (err) {
        setError('Error loading data.');
        console.error('Error loading data:', err);
        window.showAlert('danger', 'Error loading data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedBranch || !selectedDocumentType || !documentNumber) {
      window.showAlert('danger', 'Please fill branch, document type and document number.');
      return;
    }

    try {
      setIsSearching(true);
      setSearchResults(null);

      let results = [];

      if (selectedDocumentType === 'budget') {
        results = await searchBudget(selectedBranch, documentNumber);
      } else if (selectedDocumentType === 'serviceOrder') {
        results = await searchServiceOrder(selectedBranch, documentNumber);
      }

      setSearchResults(results);

      if (results.length === 0) {
        window.showAlert('info', 'Nenhum documento encontrado.');
      }
    } catch (error) {
      console.error('Error searching documents:', error);
      window.showAlert('danger', 'Error searching documents. Please try again.');
      setSearchResults(null);
    } finally {
      setIsSearching(false);
    }
  };

  const handleReset = () => {
    setSelectedBranch('');
    setSelectedCustomer('');
    setSelectedDocumentType('');
    setDocumentNumber('');
    setSearchResults(null);
  };

  const handleDocumentRelease = () => {
    // Reset form after successful release
    handleReset();
    window.showAlert('success', 'Documento liberado com sucesso!');
  };

  if (loading) return <Preloader />;

  if (error) {
    return (
      <div className="container-fluid selection-form-container themeTextColorPrimary mb-2">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
        <button className="btn btn-primary" onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="container-fluid selection-form-container themeTextColorPrimary mb-2">
      <form onSubmit={handleSubmit}>
        {/* Branch Selection */}
        <div className="row mb-3">
          <div className="col-12">
            <label htmlFor="branch-select" className="form-label">Filial:</label>
            <select
              id="branch-select"
              className="form-control"
              value={selectedBranch}
              onChange={(e) => setSelectedBranch(e.target.value)}
            >
              <option value="">Selecione</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Document Type Selection */}
        <div className="row mb-3">
          <div className="col-12">
            <label htmlFor="document-type-select" className="form-label">Tipo:</label>
            <select
              id="document-type-select"
              className="form-control"
              value={selectedDocumentType}
              onChange={(e) => setSelectedDocumentType(e.target.value)}
            >
              <option value="">Selecione</option>
              {documentTypes.map((docType) => (
                <option key={docType.id} value={docType.id}>
                  {docType.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Document Number */}
        <div className="row mb-3">
          <div className="col-12">
            <label htmlFor="document-number" className="form-label">Número do Documento:</label>
            <input
              id="document-number"
              type="text"
              className="form-control"
              value={documentNumber}
              onChange={(e) => setDocumentNumber(e.target.value)}
              placeholder=""
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="row mt-4">
          <div className="col-6">
            <button type="submit" className="btn btn-primary w-100" disabled={isSearching}>
              {isSearching ? 'Buscando...' : 'Buscar'}
            </button>
          </div>
          
          <div className="col-6">
            <button type="button" className="btn btn-secondary w-100" onClick={handleReset}>
              Limpar
            </button>
          </div>

        </div>
      </form>

      {/* Display search results */}
      {searchResults && (
        <DocumentGrid
          documents={searchResults}
          documentType={documentTypes.find(dt => dt.id === selectedDocumentType)}
          onRelease={handleDocumentRelease}
        />
      )}
    </div>
  );
};

export default SelectionForm;
